import { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleCheck,
  Copy,
  ExternalLink,
  FileText,
  Globe2,
  GraduationCap,
  Linkedin,
  Loader2,
  MessageSquareText,
  TriangleAlert,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ARTIFACT_ICONS = {
  portfolio: Globe2,
  resume: FileText,
  linkedin: Linkedin,
  sop: GraduationCap,
};

const STAGE_LABELS = {
  professional: "Professional interview",
  student: "Student interview",
  pivot: "Career-pivot interview",
};

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function artifactStatusClass(status) {
  if (["draft_ready", "ready_to_review", "ready_to_use", "ready"].includes(status)) return "ready";
  if (status === "creating") return "creating";
  if (status === "needs_attention") return "attention";
  return "pending";
}

export function OutputPackageList({ packages }) {
  if (!packages.length) {
    return (
      <div className="outputs-empty" data-testid="empty-output-packages">
        <FileText size={28} aria-hidden="true" />
        <div>
          <h2>No outputs yet</h2>
          <p>Complete an interview and Prime will assemble its portfolio, r\u00e9sum\u00e9, profile copy, and eligible student documents here.</p>
        </div>
        <NavLink className="primary-action compact" to="/interviews">Go to interviews <ArrowRight size={17} /></NavLink>
      </div>
    );
  }

  return (
    <div className="output-package-list" data-testid="output-package-list">
      {packages.map((item) => (
        <article className="output-package-card" key={item.id} data-testid={`output-package-${item.id}`}>
          <div className="output-package-card-heading">
            <div>
              <p>{STAGE_LABELS[item.source_interview_type] || "Career interview"} · {formatDate(item.source_completed_at)}</p>
              <h2>{item.source_interview_title}</h2>
            </div>
            <span className={`output-package-status ${item.status}`}>{item.status_label}</span>
          </div>

          {(item.core_profile?.role || item.core_profile?.focus || item.core_profile?.edge) && (
            <div className="output-package-profile" aria-label="Core profile">
              <span><small>Role</small>{item.core_profile.role || "Not set"}</span>
              <span><small>Focus</small>{item.core_profile.focus || "Not set"}</span>
              <span><small>Edge</small>{item.core_profile.edge || "Not set"}</span>
            </div>
          )}

          <div className="output-package-artifacts" aria-label="Available outputs">
            {item.artifacts.map((artifact) => {
              const Icon = ARTIFACT_ICONS[artifact.kind] || FileText;
              return (
                <span key={artifact.kind}>
                  <Icon size={16} aria-hidden="true" />
                  {artifact.title}
                  <small className={artifactStatusClass(artifact.status)}>{artifact.status_label}</small>
                </span>
              );
            })}
          </div>

          <NavLink className="outline-button output-package-open" to={`/outputs/${item.id}`}>
            Open outputs <ArrowRight size={18} />
          </NavLink>
        </article>
      ))}
    </div>
  );
}

export default function OutputsPage() {
  const auth = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    auth.client.get("/outputs")
      .then(({ data }) => { if (active) setPackages(data); })
      .catch(() => { if (active) setError("We couldn’t load your outputs. Please try again."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [auth.client]);

  return (
    <section className="page-grid outputs-page" data-testid="outputs-page">
      <div className="page-heading outputs-heading">
        <p className="kicker">Your career materials</p>
        <h1>Outputs</h1>
        <p>Everything Prime has created from your completed interviews, with the next action made clear.</p>
      </div>
      {loading && <div className="loading-state" data-testid="outputs-loading">Loading outputs…</div>}
      {error && <div className="error-note" role="alert" data-testid="outputs-error">{error}</div>}
      {!loading && !error && <OutputPackageList packages={packages} />}
    </section>
  );
}

function ArtifactPrimaryAction({ artifact, sourceInterviewId, onProvision, provisioning }) {
  const action = artifact.primary_action;
  if (!action) return null;

  if (action.type === "set_up_portfolio") {
    return (
      <button className="primary-action compact" type="button" onClick={onProvision} disabled={provisioning} data-testid={`artifact-action-${artifact.kind}`}>
        {provisioning ? <><Loader2 className="spin" size={17} /> Preparing draft…</> : <>{action.label} <ArrowRight size={17} /></>}
      </button>
    );
  }

  if (action.type === "review_linkedin") return null;

  if (action.href && action.external) {
    return <a className="primary-action compact" data-testid={`artifact-action-${artifact.kind}`} href={action.href} target="_blank" rel="noopener noreferrer">{action.label} <ExternalLink size={17} /></a>;
  }

  if (action.href) {
    return <NavLink className="primary-action compact" data-testid={`artifact-action-${artifact.kind}`} to={action.href}>{action.label} <ArrowRight size={17} /></NavLink>;
  }

  return sourceInterviewId ? <NavLink className="outline-button" to={`/interview/${sourceInterviewId}`}>View interview <ArrowRight size={17} /></NavLink> : null;
}

export function OutputPackageWorkspace({ packageData, onProvision, provisioning, provisionError }) {
  const [selectedKind, setSelectedKind] = useState(packageData.artifacts[0]?.kind || "");
  const [copied, setCopied] = useState(false);
  const selected = packageData.artifacts.find((item) => item.kind === selectedKind) || packageData.artifacts[0];
  const packageReady = packageData.status === "ready";

  useEffect(() => {
    if (!packageData.artifacts.some((item) => item.kind === selectedKind)) {
      setSelectedKind(packageData.artifacts[0]?.kind || "");
    }
  }, [packageData.artifacts, selectedKind]);

  const copyContent = async () => {
    if (!selected?.content) return;
    await navigator.clipboard.writeText(selected.content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section className="output-workspace" data-testid="output-package-workspace">
      <header className="output-workspace-header output-package-hero">
        <div>
          <NavLink className="output-back-link" to="/outputs"><ArrowLeft size={16} /> All outputs</NavLink>
          <p className="kicker">Output package</p>
          <h1>Your output package</h1>
          <p>Built from “{packageData.source_interview_title}” · Completed {formatDate(packageData.source_completed_at)}</p>
        </div>
        <div className="output-package-summary" aria-live="polite">
          <span className={`output-package-status ${packageData.status}`}>
            {packageReady ? <CircleCheck size={15} /> : <TriangleAlert size={15} />}{packageData.status_label}
          </span>
          <p>{packageReady ? "Every available output is ready to review." : "One or more outputs need your attention."}</p>
          <NavLink className="output-source-link" to={`/interview/${packageData.source_interview_id}`} data-testid="view-source-interview-link">
            <MessageSquareText size={16} /> View interview history
          </NavLink>
        </div>
      </header>

      <section className="core-profile-panel" aria-labelledby="core-profile-heading">
        <div>
          <p className="kicker">Shared foundation</p>
          <h2 id="core-profile-heading">Core profile</h2>
        </div>
        <dl>
          <div><dt>Role</dt><dd>{packageData.core_profile?.role || "Not established"}</dd></div>
          <div><dt>Focus</dt><dd>{packageData.core_profile?.focus || "Not established"}</dd></div>
          <div><dt>Edge</dt><dd>{packageData.core_profile?.edge || "Not established"}</dd></div>
        </dl>
      </section>

      <section className="artifact-table" role="table" aria-label="Generated outputs">
        <div className="artifact-table-head" role="row">
          <span role="columnheader">Output</span><span role="columnheader">Status</span><span role="columnheader">Details / next step</span>
        </div>
        {packageData.artifacts.map((artifact) => {
          const Icon = ARTIFACT_ICONS[artifact.kind] || FileText;
          const canInspect = Boolean(artifact.content) || artifact.primary_action?.type === "review_linkedin" || artifact.kind === "sop";
          return (
            <div className={`artifact-table-row ${selected?.kind === artifact.kind ? "is-selected" : ""}`} role="row" key={artifact.kind}>
              <div className="artifact-table-name" role="cell">
                <Icon size={24} aria-hidden="true" />
                <div><strong>{artifact.title}</strong><span>{artifact.description}</span></div>
              </div>
              <div role="cell"><span className={`artifact-state ${artifactStatusClass(artifact.status)}`}>{artifact.status_label}</span></div>
              <div className="artifact-table-next" role="cell">
                <p>{artifact.status === "needs_attention" ? "This output needs a quick recovery step before review." : artifact.description}</p>
                {artifact.primary_action?.type === "set_up_portfolio" || artifact.primary_action?.external ? (
                  <ArtifactPrimaryAction artifact={artifact} sourceInterviewId={packageData.source_interview_id} onProvision={onProvision} provisioning={provisioning} />
                ) : canInspect ? (
                  <button className="output-text-action" type="button" onClick={() => setSelectedKind(artifact.kind)} data-testid={`view-output-${artifact.kind}`}>
                    View output <ArrowRight size={16} />
                  </button>
                ) : artifact.primary_action?.href ? (
                  <ArtifactPrimaryAction artifact={artifact} sourceInterviewId={packageData.source_interview_id} onProvision={onProvision} provisioning={provisioning} />
                ) : <span className="artifact-no-action" aria-hidden="true">—</span>}
              </div>
            </div>
          );
        })}
      </section>

      {provisionError && <div className="error-note" role="alert">{provisionError}</div>}

      {selected && (selected.content || selected.kind === "sop") && (
          <article className="output-detail" data-testid={`output-detail-${selected.kind}`}>
            <div className="output-detail-heading">
              <div>
                <span className={`artifact-state ${artifactStatusClass(selected.status)}`}>{selected.status_label}</span>
                <h2>{selected.title}</h2>
                <p>{selected.description}</p>
              </div>
            </div>

            {selected.kind === "portfolio" && selected.status === "draft_ready" && (
              <div className="output-notice">This is a private draft. Prime has not confirmed it as published.</div>
            )}

            {selected.content && (
              <div className="output-copy-panel">
                <div className="output-copy-actions">
                  <span>Generated copy</span>
                  <button className="outline-button" type="button" onClick={copyContent}>
                    {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy text</>}
                  </button>
                </div>
                {selected.content.split(/\n\n+/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              </div>
            )}

            {selected.kind === "sop" && selected.status === "not_generated" && (
              <div className="output-not-started">
                <GraduationCap size={24} aria-hidden="true" />
                <div><strong>Master SOP not generated yet</strong><p>This output will use the same completed student interview—no second interview is required.</p></div>
              </div>
            )}
          </article>
        )}
      <footer className="output-package-footer"><TriangleAlert size={18} /><span>You can regenerate individual outputs from this page whenever a temporary link expires.</span></footer>
    </section>
  );
}

export function OutputPackagePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { packageId } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState("");

  const loadPackage = async () => {
    const { data } = await auth.client.get(`/output-packages/${packageId}`);
    setPackageData(data);
    return data;
  };

  useEffect(() => {
    let active = true;
    auth.client.get(`/output-packages/${packageId}`)
      .then(({ data }) => { if (active) setPackageData(data); })
      .catch((reason) => {
        if (!active) return;
        if (reason?.response?.status === 404) navigate("/outputs", { replace: true });
        else setError("We couldn’t load this output package. Please try again.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [auth.client, navigate, packageId]);

  const provisionPortfolio = async () => {
    if (!packageData) return;
    setProvisioning(true);
    setProvisionError("");
    try {
      const { data: interview } = await auth.client.post(`/interviews/${packageData.source_interview_id}/provision`);
      const refreshed = await loadPackage();
      const portfolio = refreshed.artifacts.find((item) => item.kind === "portfolio");
      const previewUrl = portfolio?.primary_action?.href || interview.portfolio_url;
      if (previewUrl) window.open(previewUrl, "_blank", "noopener,noreferrer");
    } catch {
      setProvisionError("We couldn’t prepare your portfolio draft. Please try again.");
    } finally {
      setProvisioning(false);
    }
  };

  if (loading) return <div className="loading-state" data-testid="output-package-loading">Loading your outputs…</div>;
  if (error) return <div className="error-note" role="alert">{error}</div>;
  if (!packageData) return null;

  return <OutputPackageWorkspace packageData={packageData} onProvision={provisionPortfolio} provisioning={provisioning} provisionError={provisionError} />;
}
